import { memo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Collapse } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { Text, fontWeight, textLevel } from '../../../../packages/ui/atoms/typography';
import { Checkbox } from '../../../../packages/ui/atoms/checkbox';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import { sxStyles, useStyles } from './WaitingRoomDetails.styles';
import { Icons, colors } from '../../../../packages';
import Markdown from 'markdown-to-jsx';
import {
  getChatbots,
  updateAppointmentAddMultipleEvaluations,
} from '../../../../services/conversation/conversation.service';
import EvaluationDetail from '../../priorities/appointments/evaluation-detail';
import ViewChatbot from '../../member-detail/components/Chatbots/ViewChatbot';
import AddEvaluation from '../../appointments/components/appointment-detail/AddEvaluation';
import { conversationActionCreators } from '../../../../redux/modules/conversation/actions';
import {
  selectEvaluation,
  selectAssignedEvaluationState,
} from '../../../../redux/modules/conversation/selectors';
import Diagnoses from '../../priorities/diagnoses';
import Medications from '../../priorities/medications';
import { PriorityDetailDrawer } from '../../priorities/PriorityDetailDrawer';
import { getMemberConstructsByName } from '../../../../services/profile/profile.service';
import { PrioritiesCard } from '../priority-list/PriorityList.constants';

/**
 * WaitingRoomDetails Component
 * Displays collapsible sections with member details in the waiting room
 */
const WaitingRoomDetails = memo(({ appointment, isUpcomingAppointment = false }) => {
  const classes = useStyles({ isUpcomingAppointment });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { demographicDetails } = useSelector(getProfile);
  const availableEvaluationsList = useSelector(selectEvaluation);
  const { evaluations: assignedEvaluationsData } = useSelector(selectAssignedEvaluationState);
  const [expandedSections, setExpandedSections] = useState({
    pdmp: true,
    chatbots: true,
    presentingProblem: true,
    lastSessionsummary: true,
    exercises: true,
    evaluations: true,
    diagnoses: true,
    medications: true,
  });
  const [evaluations, setEvaluations] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [showViewChatbot, setShowViewChatbot] = useState(false);
  const [showAddEvaluation, setShowAddEvaluation] = useState(false);
  const [showSearchExercises, setShowSearchExercises] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [viewAllType, setViewAllType] = useState(null);
  const [count, setCount] = useState({
    activeDiagnoses: 1,
    activeMedications: 1,
  });
  const [patientConstructs, setPatientConstructs] = useState({
    presentingProblem: '',
    lastSessionsummary: '',
  });
  const [trigger, setTrigger] = useState(false);
  const [diagnosesReset, setDiagnosesReset] = useState(false);
  const [medicationsReset, setMedicationsReset] = useState(false);
  const [symptomsReset, setSymptomsReset] = useState(false);
  const [envFactorsReset, setEnvFactorsReset] = useState(false);

  const fetchPatientConstructs = async () => {
    try {
      if (appointment?.patientId) {
        const response = await getMemberConstructsByName(
          { memberId: appointment?.patientId },
          'Last Session Summary,Presenting Problem',
        );
        if (response?.status === 200) {
          setPatientConstructs({
            presentingProblem: response?.data?.find(
              construct => construct.constructName === 'Presenting Problem',
            )?.constructValue,
            lastSessionsummary: response?.data?.find(
              construct => construct.constructName === 'Last Session Summary',
            )?.constructValue,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching patient constructs:', error);
      setPatientConstructs({
        presentingProblem: '',
        lastSessionsummary: '',
      });
    }
  };

  useEffect(() => {
    if (appointment?.appointmentId) {
      dispatch(
        conversationActionCreators.fetchAssignedEvaluations({ appointmentId: appointment?.appointmentId }),
      );
    }
  }, [appointment?.appointmentId, trigger]);

  useEffect(() => {
    if (assignedEvaluationsData) {
      setEvaluations(assignedEvaluationsData.filter(evaluation => evaluation.type !== 'EXERCISE'));
      const exercises = assignedEvaluationsData.filter(
        evaluation => evaluation.type === 'EXERCISE' && evaluation.status !== 'REJECTED',
      );
      setExercises(
        exercises.map(exercise => ({
          id: exercise.id,
          name: exercise.name,
          assignedAt: exercise.assignedAt,
          isRecommended: exercise.isRecommended,
          checked: !exercise.isRecommended,
        })),
      );
    }
  }, [assignedEvaluationsData]);

  useEffect(() => {
    if (appointment?.patientId) {
      getChatbots({ userId: appointment.patientId }, { includeResponses: false })
        .then(response => {
          if (response?.data) {
            const completedChatbots = response.data.filter(chatbot => chatbot.completed);
            setChatbots(completedChatbots);
          }
        })
        .catch(error => {
          console.error('Error fetching chatbots:', error);
        });
    }
  }, [appointment?.patientId]);

  useEffect(() => {
    void fetchPatientConstructs();
  }, [appointment?.patientId]);

  const toggleSection = section => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const onClickChatbot = latestContextId => {
    if (latestContextId) {
      dispatch(conversationActionCreators.fetchChatbotContacts(latestContextId));
      setShowViewChatbot(true);
    }
  };

  const handlePDMP = () => {
    const patientState = appointment?.patientState;
    const url =
      patientState === 'Connecticut'
        ? 'https://connecticut.pmpaware.net/login'
        : patientState === 'Texas'
        ? 'https://texas.pmpaware.net/login'
        : patientState === 'Florida'
        ? 'https://florida.pmpaware.net/login'
        : patientState === 'Virginia'
        ? 'https://virginia.pmpaware.net/login'
        : patientState === 'New Hampshire'
        ? 'https://newhampshire.pmpaware.net/login'
        : null;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleAssignEvaluation = event => {
    event.stopPropagation();
    setShowAddEvaluation(true);
  };

  const onAddEvaluation = selectedValues => {
    dispatch(
      conversationActionCreators.addAssignedEvaluation({
        evaluationIds: selectedValues,
        appointmentId: appointment?.appointmentId,
      }),
    );
    setShowAddEvaluation(false);
    setShowSearchExercises(false);
  };

  const handleSearchExercises = event => {
    event.stopPropagation();
    setShowSearchExercises(true);
  };

  const handleExerciseCheckboxChange = async (evaluationId, checked) => {
    if (checked) {
      await updateAppointmentAddMultipleEvaluations(
        { evaluationIds: [evaluationId] },
        { appointmentId: appointment?.appointmentId },
      );
    } else {
      dispatch(
        conversationActionCreators.deleteEvaluation({
          evaluationId: evaluationId,
          appointmentId: appointment?.appointmentId,
          isExercise: true,
        }),
      );
    }
    setTrigger(prev => !prev);
  };

  const pdmpStates = ['Connecticut', 'Texas', 'Florida', 'Virginia', 'New Hampshire'];
  const showPDMP = pdmpStates.includes(appointment?.patientState);
  const hasSummary = demographicDetails?.pdmpSummary && demographicDetails.pdmpSummary.trim() !== '';

  // Don't render if no PDMP, no chatbots and no evaluations, unless it's an upcoming appointment
  if (!showPDMP && chatbots.length === 0 && evaluations.length === 0 && !isUpcomingAppointment) {
    return null;
  }

  const handleExerciseDetail = (appointment, evaluationId) => {
    navigate(
      `/provider/members/${appointment?.patientId}/appointments/${appointment?.appointmentId}/exercise/${evaluationId}`,
    );
  };

  const onViewAll = type => {
    setSelectedPriority(null);
    setViewAllType(type);
  };

  const onCloseViewAllDrawer = () => {
    setViewAllType(null);
    setSelectedPriority(null);
  };

  const onElementsUpdated = type => {
    switch (type) {
      case PrioritiesCard.Diagnoses: {
        setDiagnosesReset(v => !v);
        break;
      }
      case PrioritiesCard.Medications: {
        setMedicationsReset(v => !v);
        break;
      }
      case PrioritiesCard.SymptomaticFactors: {
        setSymptomsReset(v => !v);
        break;
      }
      case PrioritiesCard.EnvironmentalFactors: {
        setEnvFactorsReset(v => !v);
        break;
      }
      default: {
        console.log('Unknown type updated');
      }
    }
  };

  const handleEvaluationDeletion = evaluationId => {
    const isAssigned = assignedEvaluationsData.some(evalId => evalId.id === evaluationId);
    if (isAssigned) {
      const { appointmentId } = appointment;
      dispatch(conversationActionCreators.deleteEvaluation({ appointmentId, evaluationId }));
    }
  };

  return (
    <Box className={classes.container}>
      {/* Presenting Problem Section */}
      {patientConstructs.presentingProblem && (
        <>
          <Box className={classes.section}>
            <Box
              className={`patientConstructs.presentingProblem !== "Not discussed." ? ${classes.constructsHeader} : ${classes.sectionHeader}`}
              onClick={() => toggleSection('presentingProblem')}
            >
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.presentingProblem ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    Presenting problem
                  </Text>
                </Box>
              </Box>
            </Box>

            <Collapse in={expandedSections.presentingProblem}>
              <Box paddingLeft="32px">
                <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral900}>
                  <Markdown>{patientConstructs.presentingProblem}</Markdown>
                </Text>
              </Box>
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {/* Last Session Summary Section */}
      {patientConstructs.lastSessionsummary && (
        <>
          <Box className={classes.section}>
            <Box
              className={`patientConstructs.lastSessionsummary !== "Not discussed." ? ${classes.constructsHeader} : ${classes.sectionHeader}`}
              onClick={() => toggleSection('lastSessionsummary')}
            >
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.lastSessionsummary ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    Last session summary
                  </Text>
                </Box>
              </Box>
            </Box>

            <Collapse in={expandedSections.lastSessionsummary}>
              <Box paddingLeft="32px">
                <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral900}>
                  <Markdown>{patientConstructs.lastSessionsummary}</Markdown>
                </Text>
              </Box>
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {/* PDMP Summary Section */}
      {showPDMP && (
        <>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('pdmp')}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.pdmp ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                PDMP summary
              </Text>
            </Box>

            <Collapse in={expandedSections.pdmp}>
              <Box className={classes.sectionContent}>
                {hasSummary ? (
                  <Box className={classes.markdownContent}>
                    <Markdown>{demographicDetails.pdmpSummary}</Markdown>
                  </Box>
                ) : (
                  <Text className={classes.descriptionText}>No PDMP summary available</Text>
                )}

                <Text className={classes.viewLink} onClick={handlePDMP}>
                  {hasSummary ? 'View' : 'Check'} PDMP
                </Text>
                {demographicDetails?.pdmpLastUpdated && (
                  <Text className={classes.lastUpdatedText}>
                    Last updated: {dayjs(demographicDetails.pdmpLastUpdated).format('MMMM D, YYYY')}
                  </Text>
                )}
              </Box>
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {/* Recently Completed Chatbots Section */}
      {chatbots.length > 0 && (
        <>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('chatbots')}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.chatbots ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                Recently completed chatbots
              </Text>
            </Box>

            <Collapse in={expandedSections.chatbots}>
              <Box className={classes.evaluationsList}>
                {chatbots.map((chatbot, index) => (
                  <Box
                    key={chatbot.conversationId || chatbot.chatbotName}
                    className={classes.chatbotCard}
                    sx={{
                      borderBottom: index === chatbots.length - 1 ? 'none' : `1px solid ${colors.neutral100}`,
                    }}
                    onClick={() => onClickChatbot(chatbot.latestContextId)}
                  >
                    <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.primary500}>
                      {chatbot.chatbotName}
                    </Text>
                    <Text level={textLevel.S} weight={fontWeight.REGULAR} color={colors.neutral500}>
                      {chatbot.completedAt ? dayjs(chatbot.completedAt).format('MMMM D, YYYY') : ''}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {/* Exercises Section */}
      {(exercises.length > 0 || (isUpcomingAppointment && exercises.length === 0)) && (
        <>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('exercises')}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.exercises ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    Exercises
                  </Text>
                  <Text level={textLevel.M} weight={fontWeight.REGULAR} color={colors.neutral500}>
                    {exercises.filter(ex => ex.checked).length} selected
                  </Text>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="6px"
                  onClick={handleSearchExercises}
                  sx={sxStyles.assignEvaluations}
                >
                  <>
                    <Icons glyph="search-outlined" color={colors.primary500} />
                    <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.primary600}>
                      Search exercises
                    </Text>
                  </>
                </Box>
              </Box>
            </Box>

            <Collapse in={expandedSections.exercises}>
              {exercises.length > 0 ? (
                <Box className={classes.evaluationsList}>
                  {exercises.map((exercise, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap="12px"
                      padding="12px"
                      backgroundColor={exercise.checked ? colors.primary50 : colors.neutral25}
                      border={`${
                        exercise.checked ? `2px solid ${colors.primary500}` : `1px solid ${colors.neutral200}`
                      }`}
                      borderRadius="8px"
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.neutral50,
                        },
                      }}
                    >
                      <Checkbox
                        checked={exercise.checked || false}
                        onChange={event => {
                          event.stopPropagation();
                          handleExerciseCheckboxChange(exercise.id, event.target.checked);
                        }}
                        color={colors.primary500}
                      />
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap="2px"
                        flex="1"
                        onChange={event => {
                          handleExerciseCheckboxChange(exercise.id, event.target.checked);
                        }}
                      >
                        <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                          {exercise.name}
                        </Text>
                        {exercise.checked ? (
                          <Text level={textLevel.S} weight={fontWeight.REGULAR} color={colors.neutral600}>
                            Assigned {dayjs(exercise.assignedAt).format('M/D/YYYY')}
                          </Text>
                        ) : (
                          <Text level={textLevel.S} weight={fontWeight.REGULAR} color={colors.neutral600}>
                            Recommended
                          </Text>
                        )}
                      </Box>
                      {isUpcomingAppointment && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          onClick={() => handleExerciseDetail(appointment, exercise.id)}
                          sx={{
                            cursor: 'pointer',
                          }}
                        >
                          <Icons glyph="arrow-right" color={colors.primary500} />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" gap="8px" padding="40px 20px">
                  <Icons
                    glyph="open-session-lottie"
                    color={colors.primary500}
                    className={classes.lottieSize}
                  />
                  <Box display="flex" flexDirection="column" alignItems="center" gap="8px">
                    <Text level={textLevel.L} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                      No Planned Exercises
                    </Text>
                    <Text
                      level={textLevel.M}
                      weight={fontWeight.REGULAR}
                      color={colors.neutral900}
                      textAlign="center"
                    >
                      Looks like no exercises are planned for this appointment yet. You can
                      <br />
                      easily add one by searching and assigning it here.
                    </Text>
                  </Box>
                </Box>
              )}
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {/* Evaluations Section */}
      {(evaluations.length > 0 || (isUpcomingAppointment && evaluations.length === 0)) && (
        <>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('evaluations')}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.evaluations ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    Evaluations
                  </Text>
                  <Text level={textLevel.M} weight={fontWeight.REGULAR} color={colors.neutral500}>
                    {evaluations.length} total
                  </Text>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  onClick={handleAssignEvaluation}
                  sx={sxStyles.assignEvaluations}
                >
                  <>
                    <Icons glyph="plus" color={colors.primary500} />
                    <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.primary600}>
                      Assign evaluation
                    </Text>
                  </>
                </Box>
              </Box>
            </Box>

            <Collapse in={expandedSections.evaluations}>
              {evaluations.length > 0 ? (
                <Box className={classes.evaluationsList}>
                  {evaluations.map((evaluation, index) => (
                    <Box
                      key={index}
                      className={classes.evaluationCard}
                      onClick={() => setSelectedEvaluation(evaluation)}
                    >
                      <Box display="flex" flexDirection="column" gap="2px">
                        <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                          {evaluation.name}
                        </Text>
                        <Text level={textLevel.S} weight={fontWeight.REGULAR} color={colors.neutral600}>
                          Assigned {dayjs(evaluation.assignedDate).format('M/D/YYYY')}
                        </Text>
                      </Box>
                      <Icons glyph="arrow-right" color={colors.primary500} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" gap="8px" padding="40px 20px">
                  <Icons glyph="lottie-case-notes" color={colors.primary500} className={classes.lottieSize} />
                  <Box display="flex" flexDirection="column" alignItems="center" gap="8px">
                    <Text level={textLevel.L} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                      No Assigned Evaluations
                    </Text>
                    <Text
                      level={textLevel.M}
                      weight={fontWeight.REGULAR}
                      color={colors.neutral900}
                      textAlign="center"
                    >
                      No evaluations have been assigned to this patient. If you think this is in
                      <br />
                      error, please add one by searching and assigning it here.
                    </Text>
                  </Box>
                </Box>
              )}
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {count.activeDiagnoses > 0 && (
        <>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('diagnoses')}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.evaluations ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    Active Diagnoses
                  </Text>
                  <Text level={textLevel.M} weight={fontWeight.REGULAR} color={colors.neutral500}>
                    {count.activeDiagnoses} total
                  </Text>
                </Box>
                <Text
                  level={textLevel.S}
                  weight={fontWeight.SEMI_BOLD}
                  color={colors.primary600}
                  onClick={event => {
                    event.stopPropagation();
                    onViewAll('diagnoses');
                  }}
                >
                  See all
                </Text>
              </Box>
            </Box>

            <Collapse in={expandedSections.diagnoses}>
              <Box paddingLeft="32px">
                <Diagnoses
                  memberId={appointment?.patientId}
                  hideNotes={false}
                  onViewAll={onViewAll}
                  onClick={() => {}}
                  reset={false}
                  isCardView={true}
                  setCount={setCount}
                />
              </Box>
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {count.activeMedications > 0 && (
        <>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('medications')}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  transform: expandedSections.evaluations ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <Icons glyph="chevron-down" color={colors.neutral700} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    Active Medications
                  </Text>
                  <Text level={textLevel.M} weight={fontWeight.REGULAR} color={colors.neutral500}>
                    {count.activeMedications} total
                  </Text>
                </Box>
                <Text
                  level={textLevel.S}
                  weight={fontWeight.SEMI_BOLD}
                  color={colors.primary600}
                  onClick={event => {
                    event.stopPropagation();
                    onViewAll('medications');
                  }}
                >
                  See all
                </Text>
              </Box>
            </Box>

            <Collapse in={expandedSections.medications}>
              <Box paddingLeft="32px">
                <Medications
                  memberId={appointment?.patientId}
                  hideNotes={false}
                  onViewAll={onViewAll}
                  onClick={() => {}}
                  reset={false}
                  isCardView={true}
                  setCount={setCount}
                />
              </Box>
            </Collapse>
          </Box>
          <Box className={classes.divider} />
        </>
      )}

      {!!viewAllType && (
        <PriorityDetailDrawer
          memberId={appointment?.patientId}
          selectedItem={selectedPriority}
          type={viewAllType}
          onClose={onCloseViewAllDrawer}
          open={!!viewAllType}
          onElementsUpdated={onElementsUpdated}
        />
      )}

      {/* Evaluation Detail Drawer */}
      {selectedEvaluation && (
        <EvaluationDetail
          isOpen={!!selectedEvaluation}
          onClose={() => setSelectedEvaluation(null)}
          evaluation={selectedEvaluation}
          memberId={appointment?.patientId}
          appointmentId={appointment?.appointmentId}
          isView={false}
        />
      )}

      {/* Chatbot Detail Drawer */}
      <ViewChatbot
        open={showViewChatbot}
        onClose={() => setShowViewChatbot(false)}
        userId={appointment?.patientId}
      />

      {/* Add Evaluation Drawer */}
      {showSearchExercises && (
        <AddEvaluation
          isOpen={showSearchExercises}
          onClose={() => setShowSearchExercises(false)}
          onAddEvaluation={onAddEvaluation}
          appointment={appointment}
          assignedEvaluations={assignedEvaluationsData.filter(
            evaluation =>
              evaluation.type === 'EXERCISE' && evaluation.status !== 'REJECTED' && !evaluation.isRecommended,
          )}
          isExercise={true}
          onRemoveEvaluation={handleEvaluationDeletion}
        />
      )}

      {/* Add Evaluation Drawer */}
      {showAddEvaluation && availableEvaluationsList?.length > 0 && (
        <AddEvaluation
          isOpen={showAddEvaluation}
          onClose={() => setShowAddEvaluation(false)}
          onAddEvaluation={onAddEvaluation}
          appointment={appointment}
          evaluations={availableEvaluationsList}
          assignedEvaluations={assignedEvaluationsData}
          onRemoveEvaluation={handleEvaluationDeletion}
        />
      )}
    </Box>
  );
});

WaitingRoomDetails.displayName = 'WaitingRoomDetails';

export { WaitingRoomDetails };
